import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import { type IAgentHierarchy, type IAgentHierarchyNode } from '../interface/peer-chat';

// Standalone client for api.core's `GET /api/v1/AgentAssignment/me/hierarchy`.
//
// Same posture as `agent-assignment-api.ts`: the core JWT rides along, there is NO 401→logout
// interceptor (this is auxiliary data — a failure must never sign an agent out), every status
// resolves so the caller can classify the body, and the call carries a timeout because the
// Redux helpers send none and a dropped socket would otherwise spin forever.

const hierarchyApi = axios.create({ baseURL: COREAPI, timeout: 15_000 });

hierarchyApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

// api.core sometimes answers JSON with a Content-Type axios will not auto-parse.
hierarchyApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

export type AgentHierarchyOutcome =
	{ reachable: false } | { reachable: true; httpStatus: number; hierarchy: IAgentHierarchy | null };

/**
 * The caller's own nodes, direct parents and direct children for one election.
 *
 * <p>api.core wraps the payload in its standard envelope — read `data`, never the root. An
 * agent with no assignment gets `electionId: 0` and three empty lists, which is a legitimate
 * answer, not an error. `electionId` omitted means "the latest election I am assigned to".</p>
 */
export const getMyHierarchy = async (electionId?: number): Promise<AgentHierarchyOutcome> => {
	try {
		const res = await hierarchyApi.get('/api/v1/AgentAssignment/me/hierarchy', {
			params: electionId && electionId > 0 ? { electionId } : undefined,
			validateStatus: () => true,
		});
		const body = res.data as { data?: unknown } | undefined;
		return {
			reachable: true,
			httpStatus: res.status,
			hierarchy: res.status >= 200 && res.status < 300 ? normalizeHierarchy(body?.data) : null,
		};
	} catch {
		return { reachable: false };
	}
};

/**
 * An independent observer's supervisor links: api.core `ObserverApplication/me/observer-hierarchy`,
 * same response shape as the agent route. An organisation's state observer supervises its observers
 * in that state; there is no party in it.
 */
export const getMyObserverHierarchy = async (electionId?: number): Promise<AgentHierarchyOutcome> => {
	try {
		const res = await hierarchyApi.get('/api/v1/ObserverApplication/me/observer-hierarchy', {
			params: electionId && electionId > 0 ? { electionId } : undefined,
			validateStatus: () => true,
		});
		const body = res.data as { data?: unknown } | undefined;
		return {
			reachable: true,
			httpStatus: res.status,
			hierarchy: res.status >= 200 && res.status < 300 ? normalizeHierarchy(body?.data) : null,
		};
	} catch {
		return { reachable: false };
	}
};

const normalizeNode = (raw: unknown): IAgentHierarchyNode | null => {
	if (!raw || typeof raw !== 'object') return null;
	const r = raw as Record<string, unknown>;
	const userId = Number(r.userId ?? 0);
	if (!userId) return null;
	return {
		userId,
		fullName: typeof r.fullName === 'string' ? r.fullName : '',
		phoneNumber: typeof r.phoneNumber === 'string' ? r.phoneNumber : null,
		entityCode: typeof r.entityCode === 'string' ? r.entityCode : '',
		level: typeof r.level === 'string' ? r.level : '',
		resultType: typeof r.resultType === 'string' ? r.resultType.toLowerCase() : '',
		isActive: r.isActive !== false,
	};
};

const normalizeNodes = (raw: unknown): IAgentHierarchyNode[] =>
	Array.isArray(raw) ? raw.map(normalizeNode).filter((n): n is IAgentHierarchyNode => n !== null) : [];

export const normalizeHierarchy = (raw: unknown): IAgentHierarchy | null => {
	if (!raw || typeof raw !== 'object') return null;
	const r = raw as Record<string, unknown>;
	return {
		partyId: Number(r.partyId ?? 0),
		electionId: Number(r.electionId ?? 0),
		self: normalizeNodes(r.self),
		parents: normalizeNodes(r.parents),
		children: normalizeNodes(r.children),
	};
};
